import React from "react";
import { useEffect, useState } from "react";
import { LoadingStateContext } from "../../Hooks/LoadingContext/LoadingContext";
import axiosInstance from "../../utilities/Axios";
import AlertDialog from "../AlertDialog/AlertDialog";
import { EDialogAction } from "../Enum/DialogActionEnum";
import ResumeDialogBox from "./ResumeDialogBox";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Hooks/ThemeContext/ThemeContext";
import { TableContainer, Paper, TableHead, TableRow, TableCell, TableBody, Button, Table, TablePagination, TableFooter, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from "@mui/material";
import { DeleteOutline, EditOutlined, OpenInNew, PersonAdd } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { RecStatusDisplayEnum } from "../../Types/Enum";
import { Can } from "../../utilities/util";

const ResumeTable = (props: { filter: any, section: string }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [resume, setResume] = useState(null);
  const [resumeList, setResumeList] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [limit, setLimit] = useState(10);
  const [editDialogVisibility, setEditDialogVisibility] = useState(false);
  const [alertDialogVisibility, setAlertDialogVisibility] = useState(false);
  const [recruitmentDialogVisibility, setRecruitmentDialogVisibility] = useState(false);
  const [dialogAction, setDialogAction] = useState(EDialogAction.view);
  const [value, setValue] = useState({});
  const { changeLoading, isLoading } = React.useContext(LoadingStateContext);

  useEffect(() => {
    getResumeList();
  }, [page, limit, props.filter]);

  const getResumeList = async () => {
    try {
      changeLoading(true);
      const response = await axiosInstance.get(
        `/v1/resume?type_dom_corp=${theme.theme}&page=${page + 1}&items_per_page=${limit}`,
        {
          params: props.filter,
        }
      );

      const data = response.data;
      setResumeList(data.result.data);

      setTotalItems(data.result.total);
    } catch (err: any) {
      enqueueSnackbar(
        `${err.response.data.error}`,
        {
          variant: "error",
        }
      );
    } finally {
      changeLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getResumeById = async (resumeId: number, action: EDialogAction) => {
    try {
      const response = await axiosInstance.get(`/v1/resume/${resumeId}`, {});

      const data = response.data;

      setResume(data.result);

      if (action == EDialogAction.edit) {
        setEditDialogVisibility(true);
      } else if (action == EDialogAction.view) {
        setEditDialogVisibility(true);
      } else if (action == EDialogAction.delete) {
        setAlertDialogVisibility(true);
      } else if (action == EDialogAction.recruitment) {
        setRecruitmentDialogVisibility(true);
      }
      setDialogAction(action);
    } catch (err) {
      console.log(err);
    }
  };

  const goToNextPage = () => {
    setPage(page + 1);
  };

  const goToPreviousPage = () => {
    setPage(page - 1);
  };

  function editDialogClose(action: EDialogAction) {
    setEditDialogVisibility(false);
    if (action == EDialogAction.edit) {
      setValue({});
    } else if (action == EDialogAction.view) {
    } else if (action == EDialogAction.delete) {
    }
    getResumeList();
  }
  function alertDialogClose(action: EDialogAction) {
    setAlertDialogVisibility(false);
    getResumeList();
  }

  const handleRecruitmentDialogClose = (confirmed: boolean, resumeReq?: any) => {
    setRecruitmentDialogVisibility(false);
    if (confirmed) {
      axiosInstance.post(`/v1/resume/recruitment/${resumeReq.id}`, {})
        .then(() => {
          enqueueSnackbar('Recruitment sent successfully!', { variant: 'success' });
        })
        .catch((err) => {
          console.error(err);
          enqueueSnackbar('Error sending recruitment', { variant: 'error' });
        })
        .finally(() => {
          getResumeList();
        });
    }
  };
  const pageChangeHandler = (resumeId: any) => {
    navigate({
      pathname: "/admin/pdf",
      search: `?resumeId=${resumeId}`,
    });
  };

  return (
    <>
      {resume && (<ResumeDialogBox
        open={editDialogVisibility}
        onClose={editDialogClose}
        data={resume}
        dialogAction={dialogAction}
      />)}
      {resume && (<AlertDialog
        openAlert={alertDialogVisibility}
        onCloseAlert={alertDialogClose}
        data={resume}
        action="resume"
      />)}
      {resume && (
        <Dialog
          open={recruitmentDialogVisibility}
          onClose={() => handleRecruitmentDialogClose(false)}
          aria-labelledby="recruitment-dialog-title"
          aria-describedby="recruitment-dialog-description"
        >
          <DialogTitle id="recruitment-dialog-title">Confirm Recruitment</DialogTitle>
          <DialogContent>
            <DialogContentText id="recruitment-dialog-description">
              Are you sure you want to send recruitment for this resume?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleRecruitmentDialogClose(false)} color="primary" variant="contained">
              Cancel
            </Button>
            <Button onClick={() => handleRecruitmentDialogClose(true, resume)} color="secondary" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
      <Paper>
        <TableContainer component={Paper}>
          <Table aria-label="resume table">
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>First Name</TableCell>
                <TableCell>Last Name</TableCell>
                <TableCell>Passport Number</TableCell>
                <TableCell>Mobile Number</TableCell>
                {props.filter && props.filter?.rec_status && (
                  <>
                    <TableCell align="center">Job Title</TableCell>
                    <TableCell align="center">Recruitment</TableCell>
                  </>
                )}
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && resumeList && resumeList.map((data: any, key: number) => (
                <TableRow key={data.id}>
                  <TableCell>{data.id}</TableCell>
                  <TableCell>{data.customer_reference}</TableCell>
                  <TableCell>{new Date(data.created_at).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })}</TableCell>
                  <TableCell>{data.first_name}</TableCell>
                  <TableCell>{data.last_name}</TableCell>
                  <TableCell>{data.passport_no}</TableCell>
                  <TableCell>{data.mobile_no1}</TableCell>
                  {props.filter && props.filter?.rec_status && (
                    <>
                      <TableCell align="center">{data.base_job_title}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={RecStatusDisplayEnum[data.rec_status].label}
                          color={RecStatusDisplayEnum[data.rec_status].color}
                        />
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    {/* <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        pageChangeHandler(data.id);
                      }}
                    >
                      <OpenInNew />
                    </Button>{" "} */}                    
                    {Can(props.section, ["VIEW", "EDIT", "RECEPTION_EDIT"]) && (
                      <>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          disabled={!(props.filter && props.filter?.rec_status) && data.rec_status !== "0"}
                          onClick={() => {
                            getResumeById(data.id, EDialogAction.edit);
                          }}
                        >
                          <EditOutlined />
                        </Button>{" "}
                      </>
                    )}
                    {(Can('recruitments', ["ADD"]) && !(props.filter && props.filter?.rec_status)) && (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => {
                          getResumeById(data.id, EDialogAction.recruitment);
                        }}
                        disabled={data.rec_status !== "0"}
                      >
                        <PersonAdd />
                      </Button>
                    )}{" "}
                    {/* <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={() => {
                        getResumeById(data.id, EDialogAction.delete);
                      }}
                    >
                      <DeleteOutline />
                    </Button> */}
                    {Can('resume', ["VIEW"]) && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => {
                            navigate(`/admin/resume-view/${data.id}`);
                          }}
                        >
                          <OpenInNew />
                        </Button>{" "}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && resumeList && resumeList.length == 0 && (
                <TableRow>
                  <TableCell colSpan={7} >
                    No Data Found
                  </TableCell>
                </TableRow>
              )}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              count={totalItems}
              rowsPerPage={limit}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Table>
        </TableContainer>

      </Paper>
    </>
  );

};

export default ResumeTable;
